/**
 * 功能检索语料（模块功能地图的搜索投影，keyed by Feature id 或 `<featureId>#<tabKey>`）。
 *
 * 为什么单独一份而不是写进 `features.ts`：与 `navTabs.ts` 同理——Registry 管「功能是什么、在哪」，
 * 这里管「用户会怎么称呼它」。语料是可以整批增删的检索素材，与结构定义分开放，评审与维护都更轻。
 * 键必须能被检索索引解析到（见 `featureSearch.ts`）：功能条目用 Feature id、页内 Tab 用 `<id>#<tabKey>`，
 * 孤儿键与漏登记（有侧栏入口却没有语料）都由 `scripts/check-feature-index.mjs` 报错。
 *
 * 三类字段各有用处：
 * - `desc`：一句话「这是什么」，用于结果行展示，同时是可搜字段（用户描述得出来、说不准名字时的入口）；
 * - `keywords`：别名 / 俗称 / 旧叫法（「拉黑」「话单」「敏感词」），这是「不知道叫什么」的主要解法；
 * - `actions`：在这一页能干的关键动作（动词诉求：「导出话单」「批量导入号码」）。
 */

import { FEATURE_COPY_PART_1 } from './featureCopy/_part-1';
import { FEATURE_COPY_PART_2 } from './featureCopy/_part-2';

export interface FeatureCopy {
  /** 一句话：这是什么、用来干什么（**必填**：每个可见条目都要能回答这个问题） */
  desc: string;
  /** 别名 / 俗称 / 行业说法 / 旧叫法（侧栏入口、锚点、面板要求 ≥3 条；页内 Tab 按需给，给了就别只给一两个） */
  keywords?: string[];
  /** 该页的关键动作（动词诉求） */
  actions?: string[];
}

/** 全局动作：不是页面、没有路由，但用户会来搜（搜到就给「在哪儿」） */
export interface GlobalAction {
  id: string;
  title: string;
  /** 位置说明（点结果时给出） */
  where: string;
  keywords: string[];
}

export const FEATURE_COPY: Record<string, FeatureCopy> = {
  ...FEATURE_COPY_PART_1,
  ...FEATURE_COPY_PART_2,
};

/**
 * 全局动作：不在任何页面里，但用户会来搜「在哪改密码」。
 * 这类条目没有路由，命中后只回答「在哪儿」——功能地图不该假装能跳过去。
 */
export const GLOBAL_ACTIONS: GlobalAction[] = [
  {
    id: 'action.change-password',
    title: '修改密码',
    where: '右上角头像菜单 →「修改密码」',
    keywords: ['改密码', '换密码', '修改密码', '密码忘了', '重置我的密码'],
  },
  {
    id: 'action.logout',
    title: '退出登录',
    where: '右上角头像菜单 →「退出登录」',
    keywords: ['退出', '登出', '退出登录', '注销账号'],
  },
  {
    id: 'action.ui-settings',
    title: '界面设置（主题 / 线框 / 圆角 / 内容宽度 / 动效 / 提醒）',
    where: '左侧菜单左下角「设置」按钮 →「界面设置」抽屉',
    keywords: ['主题', '换肤', '配色', '暗色', '线框', '圆角', '界面设置', '深色模式', '铃声', '音量', '提醒'],
  },
  {
    id: 'action.agent-state',
    title: '坐席状态切换（示闲 / 小休 / 离线）',
    where: '顶栏左侧的状态胶囊（小休可二级选原因）',
    keywords: ['示闲', '小休', '离线', '坐席状态', '切换状态', '休息'],
  },
  {
    id: 'action.dial',
    title: '快捷拨号 / 接听当前来电',
    where: '顶栏「快捷拨号」条（通话中变为通话信息条）',
    keywords: ['拨号', '打电话', '外呼', '接听', '呼出', '拨打电话'],
  },
  {
    id: 'action.notifications',
    title: '通知与广播',
    where: '顶栏铃铛「通知中心」',
    keywords: ['通知', '消息', '铃铛', '广播', '公告', '提醒'],
  },
  {
    id: 'action.module-map',
    title: '模块功能地图（找功能）',
    where: '顶栏「帮助」或快捷键 Ctrl+M',
    keywords: ['找功能', '功能在哪', '功能地图', '模块地图', '帮助', '导航'],
  },
];
